import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check, KeyRound, MapPin, Users, Copy } from 'lucide-react';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function UsersPage() {
  const [users, setUsers]     = useState([]);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);  // 'create'|'edit'|'territory'|'reset'
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [newOTP, setNewOTP]   = useState('');
  const [search, setSearch]   = useState('');

  // Territory state
  const [districts, setDistricts]           = useState([]);
  const [allPincodes, setAllPincodes]       = useState([]);
  const [filteredPincodes, setFilteredPincodes] = useState([]);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [territDistrictFilter, setTerritDistrictFilter] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([api.get('/users'), api.get('/roles')]);
      setUsers(u.data.data);
      setRoles(r.data.data.filter(r => r.is_active));
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u =>
    u.full_name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // ── Create ──────────────────────────────────────────
  const openCreate = () => {
    setSelected(null);
    setNewOTP('');
    reset({ full_name:'', email:'', mobile:'', role_id:'' });
    setModal('create');
  };

  const onCreateSubmit = async (values) => {
    try {
      const { data } = await api.post('/users', values);
      setNewOTP(data.data.temporaryPassword);
      toast.success('User created');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  // ── Edit ────────────────────────────────────────────
  const openEdit = (user) => {
    setSelected(user);
    setNewOTP('');
    reset({ full_name: user.full_name, email: user.email, mobile: user.mobile||'', role_id: user.role_id, is_active: !!user.is_active });
    setModal('edit');
  };

  const onEditSubmit = async (values) => {
    try {
      await api.put(`/users/${selected.id}`, values);
      toast.success('User updated');
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  // ── Reset password ──────────────────────────────────
  const doReset = async () => {
    try {
      const { data } = await api.post(`/users/${selected.id}/reset-password`);
      setNewOTP(data.data.temporaryPassword);
      toast.success('Password reset');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  // ── Territory ───────────────────────────────────────
  const openTerritory = async (user) => {
    setSelected(user);
    try {
      const [d, p, t] = await Promise.all([
        api.get('/masters/districts?active=true'),
        api.get('/masters/pincodes?active=true'),
        api.get(`/users/${user.id}/territories`),
      ]);
      setDistricts(d.data.data);
      setAllPincodes(p.data.data);
      setFilteredPincodes(p.data.data);
      setSelectedPincodes(t.data.data.map(x => x.pincode_id));
    } catch { toast.error('Failed to load territory data'); }
    setModal('territory');
  };

  useEffect(() => {
    if (territDistrictFilter) {
      setFilteredPincodes(allPincodes.filter(p => String(p.district_id) === territDistrictFilter));
    } else {
      setFilteredPincodes(allPincodes);
    }
  }, [territDistrictFilter, allPincodes]);

  const togglePincode = (id) => {
    setSelectedPincodes(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const saveTerritories = async () => {
    try {
      await api.post(`/users/${selected.id}/territories`, { pincode_ids: selectedPincodes });
      toast.success('Territories saved');
      setModal(false);
    } catch { toast.error('Failed to save'); }
  };

  // ── Delete ──────────────────────────────────────────
  const doDelete = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  const copyOTP = () => {
    navigator.clipboard.writeText(newOTP);
    toast.success('Copied to clipboard');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage admin portal accounts</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Add User</button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-bar" style={{ minWidth: 280 }}>
            <Users size={14}/>
            <input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className="badge badge-orange">{users.length} users</span>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader"/></div> : (
            <table className="data-table">
              <thead><tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="user-avatar" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
                          {u.full_name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{u.full_name}</div>
                          {u.is_first_login ? <span className="badge badge-orange" style={{fontSize:10}}>Must change pwd</span> : null}
                        </div>
                      </div>
                    </td>
                    <td style={{ color: 'var(--grey-500)', fontSize: 13 }}>{u.email}</td>
                    <td><span className="badge badge-blue">{u.role_display_name}</span></td>
                    <td>{u.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">Inactive</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--grey-400)' }}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-IN') : 'Never'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(u)}><Pencil size={14}/></button>
                        <button className="btn-icon" title="Territory" onClick={() => openTerritory(u)}><MapPin size={14}/></button>
                        <button className="btn-icon" title="Reset Password" onClick={() => { setSelected(u); setNewOTP(''); setModal('reset'); }}><KeyRound size={14}/></button>
                        <button className="btn-icon" title="Delete" onClick={() => setConfirm(u.id)}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={6}>
                    <div className="empty-state"><Users size={32}/><h3>No users found</h3></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Create User Modal ─────────────────── */}
      {modal === 'create' && (
        <div className="modal-overlay" onClick={() => !newOTP && setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{newOTP ? '🎉 User Created' : 'Create User'}</span>
              {!newOTP && <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>}
            </div>
            {newOTP ? (
              <div className="modal-body">
                <div className="alert alert-success" style={{ marginBottom: 16 }}>
                  User created successfully. Share this one-time password with them.
                </div>
                <p style={{ fontSize: 13, color: 'var(--grey-600)', marginBottom: 8 }}>Temporary Password:</p>
                <div className="otp-reveal">{newOTP}</div>
                <p style={{ fontSize: 11, color: 'var(--grey-400)' }}>
                  ⚠ The user will be forced to change this on first login. This password will not be shown again.
                </p>
                <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: 'none' }}>
                  <button className="btn btn-secondary" onClick={copyOTP}><Copy size={14}/> Copy</button>
                  <button className="btn btn-primary" onClick={() => setModal(false)}>Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onCreateSubmit)}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">Full Name <span className="req">*</span></label>
                    <input className={`form-control ${errors.full_name?'error':''}`}
                      {...register('full_name',{required:'Required'})} />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Email <span className="req">*</span></label>
                      <input type="email" className={`form-control ${errors.email?'error':''}`}
                        {...register('email',{required:'Required'})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Mobile</label>
                      <input className="form-control" {...register('mobile')} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role <span className="req">*</span></label>
                    <select className={`form-control ${errors.role_id?'error':''}`}
                      {...register('role_id',{required:'Required'})}>
                      <option value="">Select role…</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.display_name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary"><Check size={14}/> Create User</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ─── Edit Modal ────────────────────────── */}
      {modal === 'edit' && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Edit User</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onEditSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name <span className="req">*</span></label>
                  <input className={`form-control ${errors.full_name?'error':''}`} {...register('full_name',{required:'Required'})} />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Email <span className="req">*</span></label>
                    <input type="email" className={`form-control ${errors.email?'error':''}`} {...register('email',{required:'Required'})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mobile</label>
                    <input className="form-control" {...register('mobile')} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Role <span className="req">*</span></label>
                  <select className="form-control" {...register('role_id',{required:'Required'})}>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.display_name}</option>)}
                  </select>
                </div>
                <label className="toggle">
                  <input type="checkbox" {...register('is_active')} />
                  <span className="toggle-track"><span className="toggle-thumb"/></span>
                  <span style={{fontSize:13}}>Account Active</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Reset Password Modal ──────────────── */}
      {modal === 'reset' && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><KeyRound size={18}/> Reset Password</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              {newOTP ? (
                <>
                  <div className="alert alert-success" style={{ marginBottom: 16 }}>Password has been reset.</div>
                  <p style={{ fontSize: 13, color: 'var(--grey-600)', marginBottom: 8 }}>New Temporary Password:</p>
                  <div className="otp-reveal">{newOTP}</div>
                  <p style={{ fontSize: 11, color: 'var(--grey-400)' }}>User must change this on next login.</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 14, color: 'var(--grey-600)', marginBottom: 20 }}>
                    Reset password for <strong>{selected?.full_name}</strong>?
                    A new temporary password will be generated.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={doReset}><KeyRound size={14}/> Reset</button>
                  </div>
                </>
              )}
              {newOTP && (
                <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={copyOTP}><Copy size={14}/> Copy</button>
                  <button className="btn btn-primary" onClick={() => setModal(false)}>Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Territory Modal ───────────────────── */}
      {modal === 'territory' && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><MapPin size={18}/> Territory: {selected?.full_name}</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 12 }}>
                Assign pincodes to this user. They will only see schools in their assigned pincodes.
              </div>
              <div className="form-group">
                <label className="form-label">Filter by District</label>
                <select className="form-control" value={territDistrictFilter} onChange={e => setTerritDistrictFilter(e.target.value)}>
                  <option value="">All Districts</option>
                  {districts.map(d => <option key={d.id} value={d.id}>{d.district_name} — {d.state_name}</option>)}
                </select>
              </div>
              <div style={{
                border: '1px solid var(--grey-200)', borderRadius: 8,
                maxHeight: 300, overflowY: 'auto', padding: 12,
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8,
              }}>
                {filteredPincodes.map(p => (
                  <label key={p.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    background: selectedPincodes.includes(p.id) ? 'var(--orange-50)' : 'transparent',
                    border: `1.5px solid ${selectedPincodes.includes(p.id) ? 'var(--orange-300)' : 'var(--grey-200)'}`,
                    borderRadius: 6, padding: '6px 10px', fontSize: 12,
                  }}>
                    <input type="checkbox"
                      checked={selectedPincodes.includes(p.id)}
                      onChange={() => togglePincode(p.id)}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--orange-600)' }}>{p.pincode}</div>
                      <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{p.area_name || p.district_name}</div>
                    </div>
                  </label>
                ))}
                {!filteredPincodes.length && <p style={{ color: 'var(--grey-400)', fontSize: 13, gridColumn: '1/-1' }}>No pincodes found</p>}
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--grey-400)' }}>
                {selectedPincodes.length} pincode{selectedPincodes.length !== 1 ? 's' : ''} selected
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTerritories}><Check size={14}/> Save Territories</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!confirm} title="Delete User" message="This will permanently remove the user and all their territory assignments."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
